import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, ArrowRight, KeyRound, Eye, EyeOff, ArrowLeft, Copyright } from "lucide-react";
import companyLogo from '../../assets/logo/AcrossLogo.png';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../components/ui/input-otp";
import loginBanner from '../../assets/logo/login-banner.png';
import "./index.scss";
import { containerVariants, itemVariants, useLoginData } from "./LoginData";

const Login = ({ onLogin }) => {
  const navigate = useNavigate();

  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    togglePasswordVisibility,
    isLoading,
    handleSubmit,
    showOtpForm,
    otp,
    hasOtpError,
    otpErrorMessage,
    handleVerifyOtp,
    handleOtpChange,
    handleBackToLogin,
  } = useLoginData(onLogin, navigate);

  return (

    <div className="login-page-new">

      {/* Left Panel - Form */}
      <div className="login-left-panel">
        {/* <div className="company-logo-container">
          <img src={companyLogo}
            alt="conpany-logo"
            className="conpany-logo"
          />
          <div /> */}

        <AnimatePresence mode="wait">
          {!showOtpForm ? (
            // Login Form
            <motion.div
              key="login-form"
              variants={containerVariants}
              initial="hidden"
              animate="show"
              exit="hidden"
              className="login-content"
            >
              <motion.div variants={itemVariants} className="login-header">
                <div className="icon-wrapperr">
                  <div className="company-logo-container">
                    <img className="company-logo" src={companyLogo} alt="Company Logo" />
                  </div>
                  <h1 className="login-title">Welcome Back</h1>
                  <p className="login-subtitle">
                    {/* Add your email <b>or</b> password to go to the next step */}
                    Sign to access the Client & Vendor OnBoarding Portal
                  </p>
                </div>
              </motion.div>

              <motion.form
                variants={itemVariants}
                onSubmit={handleSubmit}
                className="login-form-new"
              >
                <div className="form-field">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="input-wrapper">
                    <Mail className="input-icon" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="form-field">
                  <Label htmlFor="password">Password</Label>
                  <div className="input-wrapper password-wrapper">
                    <KeyRound className="input-icon" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="toggle-icon" />
                      ) : (
                        <Eye className="toggle-icon" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="button-content">
                      <div className="spinner" />
                      Processing...
                    </span>
                  ) : (
                    <span className="button-content">
                      Continue
                      <ArrowRight className="button-icon" />
                    </span>
                  )}
                </Button>
              </motion.form>
            </motion.div>
          ) : (
            // OTP Verification Form
            <motion.div
              key="otp-form"
              variants={containerVariants}
              initial="hidden"
              animate="show"
              exit="hidden"
              className="login-content"
            >
              <motion.div variants={itemVariants}>
                <Button
                  variant="ghost"
                  onClick={handleBackToLogin}
                  className="back-button"
                  disabled={isLoading}
                >
                  <ArrowLeft className="back-icon" />
                  Back
                </Button>
              </motion.div>

              <motion.div variants={itemVariants} className="login-header">
                <div className="icon-wrapperr">
                  <div className="company-logo-container">
                    <img className="company-logo" src={companyLogo} alt="Company Logo" />
                  </div>
                </div>
                <h1 className="login-title">Verify OTP</h1>
                <p className="login-subtitle">
                  We've sent a 6-digit code to <span className="email-highlight">{email}</span>
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="login-form-new">
                <div className="form-field">
                  <Label htmlFor="otp">Enter OTP Code</Label>
                  <div className={`otp-input-container ${hasOtpError ? 'error' : ''}`}>
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={handleOtpChange}
                    >
                      <InputOTPGroup className="otp-group">
                        <InputOTPSlot index={0} className="otp-slot" />
                        <InputOTPSlot index={1} className="otp-slot" />
                        <InputOTPSlot index={2} className="otp-slot" />
                        <InputOTPSlot index={3} className="otp-slot" />
                        <InputOTPSlot index={4} className="otp-slot" />
                        <InputOTPSlot index={5} className="otp-slot" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {hasOtpError && otpErrorMessage && (
                    <div className="error-message">
                      <span className="error-icon">⚠️</span>
                      <span className="error-text">{otpErrorMessage}</span>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleVerifyOtp}
                  className="submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="button-content">
                      <div className="spinner" />
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Continue'
                  )}
                </Button>
              </motion.div>

              <motion.div variants={itemVariants} className="login-footer">
                <p className="security-text">🔒 Your code expires in 15 minutes</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer - Left side only */}
        <div className="footer-layout-left-side">
          <Copyright size={16} />
          <span>Copyright 2026 Across Assist</span>
        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="login-right-panel">
        <div
          className="background-image"
          style={{
            backgroundImage: `url(${loginBanner})`,
          }}
        />
        <div className="overlay" />


        <div className="quote-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="quote-card"
          >
            <p className="quote-text">
              {!showOtpForm
                ? "Welcome to the Client & Vendor Onboarding Portal"
                : "One-time codes for maximum security and peace of mind."
              }
            </p>
            <p className="quote-subtext">
              {!showOtpForm
                ? "Effortlessly onboard as a client or vendor — your gateway to streamlined, secure collaboration starts here."
                : "Your security is our top priority"
              }
            </p>
          </motion.div>
        </div>
      </div>


      {/* left side only Footer after form  */}
      <div className="footer-layout-left-side">
        <Copyright size={16} />
        <span> 2026 Across Assist</span>
      </div>

    </div >
  );
};

export default Login;

